import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import { ADDRESS_DISPLAY_CONST } from '@constants/constant';
import { CountryList } from '@enums/application.enums';
import { AlertModalComponent } from '@components/alert-modal/alert-modal.component';
import { VENUE_ADDRESS_DISPLAY_LABELS } from './venue-address-display.constant';
import { CommonService } from '@shared/services/common.service';

export type VenueAddressDisplayInput =
  | Record<string, unknown>
  | null
  | undefined;

function trimmed(value: unknown): string {
  if (value == null) {
    return '';
  }
  return String(value).trim();
}

function firstNonEmpty(row: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const part = trimmed(row[key]);
    if (part) {
      return part;
    }
  }
  return '';
}

@Component({
  selector: 'app-venue-address-display',
  standalone: true,
  imports: [CommonModule, AlertModalComponent],
  templateUrl: './venue-address-display.component.html',
  styleUrls: ['./venue-address-display.component.scss']
})
export class VenueAddressDisplayComponent {
  readonly LABELS = VENUE_ADDRESS_DISPLAY_LABELS;

  @ViewChild('venueLocationModal') venueLocationModal: AlertModalComponent;
  @Input() showVenueName = true;
  @Input() emphasizeFirstLine = false;
  @Input() address: VenueAddressDisplayInput;
  @Input() showState: boolean = false;
  @Input() showCountry: boolean = false;
  @Input() showZip: boolean = false;
  @Input() showCity: boolean = false;
  @Input() showStreet: boolean = false;
  @Input() showMapIcon: boolean = false;
  @Input() showMapIconOnLeft: boolean = true;
  @Input() mapIconConfig: {
    showMapIcon: boolean;
    showMapIconOnLeft: boolean;
    fontSize: string;
  } = { showMapIcon: false, showMapIconOnLeft: true, fontSize: '18px' };
  @Input() showDirectionIcon: boolean = false;
  @Input() allowModalOpening: boolean = true;

  completeFormattedAddress: Array<string> = [];
  headerLabel: string = this.LABELS.EVENT_DETAILS;
  venueTime: string = '';

  constructor(private readonly cs: CommonService) {}

  get venueLine(): string {
    if (!this.showVenueName || !this.row) {
      return '';
    }
    return firstNonEmpty(this.row, [
      ADDRESS_DISPLAY_CONST.venue_name,
      ADDRESS_DISPLAY_CONST.venueName
    ]);
  }

  get streetLine(): string {
    if (!this.showStreet || !this.row) {
      return '';
    }
    return firstNonEmpty(this.row, [
      ADDRESS_DISPLAY_CONST.street_address,
      ADDRESS_DISPLAY_CONST.streetAddress
    ]);
  }

  get modalVenueName(): string {
    if (!this.row) {
      return '';
    }
    return firstNonEmpty(this.row, [
      ADDRESS_DISPLAY_CONST.venue_name,
      ADDRESS_DISPLAY_CONST.venueName
    ]);
  }

  getVenueTime() {
    if (!this.row?.event_time) {
      return;
    }
    return `${this.cs.formatTo12HourTime(
      this.row?.event_time as string
    )} ${this.row?.timezone ? this.cs.getTimeZoneAbbreviationFormat(this.row?.timezone as string) : ''}`.trim();
  }

  get modalFullAddressLines(): string[] {
    if (!this.row) {
      return [];
    }
    this.venueTime = this.getVenueTime();
    if(this.row?.event_name) {
      this.headerLabel = this.row?.event_name as string + ' ' + this.LABELS.DETAILS;
    }
    const lines: string[] = [];
    const street = firstNonEmpty(this.row, [
      ADDRESS_DISPLAY_CONST.street_address,
      ADDRESS_DISPLAY_CONST.streetAddress
    ]);
    if (street) {
      lines.push(street);
    }
    const city = trimmed(this.row[ADDRESS_DISPLAY_CONST.city]);
    const state = trimmed(this.row[ADDRESS_DISPLAY_CONST.state]);
    const zip = firstNonEmpty(this.row, [
      ADDRESS_DISPLAY_CONST.postal_code,
      ADDRESS_DISPLAY_CONST.postalCode,
      ADDRESS_DISPLAY_CONST.zip,
      ADDRESS_DISPLAY_CONST.zip_code,
      ADDRESS_DISPLAY_CONST.zipCode
    ]);
    const country = trimmed(this.row[ADDRESS_DISPLAY_CONST.country]);
    const localityParts: string[] = [];
    if (city) {
      localityParts.push(city);
    }
    if (state) {
      localityParts.push(state);
    }
    if (zip) {
      localityParts.push(zip);
    }
    if (localityParts.length) {
      lines.push(localityParts.join(', '));
    }
    const hideCountry =
      country.toLowerCase() === CountryList.UNITED_STATES.toLowerCase();
    if (country && !hideCountry) {
      lines.push(country);
    }
    return lines;
  }

  get canOpenLocationModal(): boolean {
    return !!(
      this.mapIconConfig?.showMapIcon &&
      this.row &&
      (this.modalVenueName ||
        this.modalFullAddressLines.length ||
        this.mapsDestinationQuery)
    );
  }

  get cityStateZipCountryLine(): string {
    if (!this.row) {
      return '';
    }
    const parts: string[] = [];
    const city = trimmed(this.row[ADDRESS_DISPLAY_CONST.city]);
    const state = trimmed(this.row[ADDRESS_DISPLAY_CONST.state]);
    const zip = firstNonEmpty(this.row, [
      ADDRESS_DISPLAY_CONST.postal_code,
      ADDRESS_DISPLAY_CONST.postalCode,
      ADDRESS_DISPLAY_CONST.zip,
      ADDRESS_DISPLAY_CONST.zip_code,
      ADDRESS_DISPLAY_CONST.zipCode
    ]);
    const country = trimmed(this.row[ADDRESS_DISPLAY_CONST.country]);
    const hideCountry =
      country.toLowerCase() === CountryList.UNITED_STATES.toLowerCase();
    if (this.showCity && city) {
      parts.push(city);
    }
    if (this.showState && state) {
      parts.push(state);
    }
    if (this.showZip && zip) {
      parts.push(zip);
    }
    if (this.showCountry && country && !hideCountry) {
      parts.push(country);
    }
    return parts.join(', ');
  }

  private get row(): Record<string, unknown> | null {
    const v = this.address;
    if (!v || typeof v !== 'object' || Array.isArray(v)) {
      return null;
    }
    return v as Record<string, unknown>;
  }

  openLocationModal(event?: Event): void {
    event?.stopPropagation();
    this.completeFormattedAddress = this.modalFullAddressLines;
    if (!this.canOpenLocationModal) {
      return;
    }
    this.venueLocationModal?.openModal();
  }

  onGetDirections(): void {
    const url = this.googleMapsDirectionsUrl();
    if (url && typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  private googleMapsDirectionsUrl(): string | null {
    const dest = this.mapsDestinationQuery;
    if (!dest) {
      return null;
    }
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
      dest
    )}`;
  }

  private get mapsDestinationQuery(): string | null {
    if (!this.row) {
      return null;
    }
    const lat = firstNonEmpty(this.row, ['latitude', 'lat']);
    const lng = firstNonEmpty(this.row, [
      'longitude',
      'lng',
      'long',
      'lon'
    ]);
    if (lat && lng) {
      return `${lat},${lng}`;
    }
    const lines = this.modalFullAddressLines;
    if (lines.length) {
      return lines.join(', ');
    }
    const venue = this.modalVenueName;
    return venue || null;
  }
}
